import config from 'client/shared/config';

/**
 * Force-clear every auth-related cookie in the browser.
 *
 * Why this exists
 * ---------------
 * Amplify's `signOut()` only reliably clears cookies it wrote for the current
 * subdomain. Cognito cookies land on `.muzado.com` when the app is served
 * from any `*.muzado.com` host, which means a signOut on `artists.muzado.com`
 * can leave the same cookies visible to `members.muzado.com` (and vice
 * versa). It also doesn't touch the API's koa session cookie. Net effect:
 * "log out", refresh, still logged in as the previous user.
 *
 * This utility enumerates the current document.cookie, matches any Cognito
 * `CognitoIdentityServiceProvider.*` cookie plus the koa session cookies,
 * and expires them with BOTH the parent-domain and host-only forms so we
 * catch whichever the browser wrote.
 */
export default function clearAuthCookies () {
	const parentDomain = config.cookieDomain; // '.muzado.com' in int/prod, 'localhost' in dev

	const shouldClear = (name) =>
		name.startsWith('CognitoIdentityServiceProvider.') ||
		name === 'koa:sess' ||
		name === 'koa:sess.sig';

	document.cookie.split(';').forEach((raw) => {
		const name = raw.trim().split('=')[0];
		if (!name || !shouldClear(name)) return;

		// Expire with the parent domain (matches how Cognito's SDK writes cookies
		// when the app is on a subdomain) AND without a domain attribute (host-only
		// fallback). Both must be attempted — browsers won't delete a cookie unless
		// the delete request matches its exact domain scope.
		const past = 'Thu, 01 Jan 1970 00:00:00 GMT';
		document.cookie = `${name}=; path=/; domain=${parentDomain}; expires=${past}`;
		document.cookie = `${name}=; path=/; expires=${past}`;
	});
}
